import { Injectable } from '@angular/core';
import { ISocketMessage } from 'modelos/src';
import { Observable, Subject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { websocketPath, websocketServer } from 'src/environments/environment';
import { LoginService } from '../modulos/login/login.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket?: Socket;
  private socketMsg$ = new Subject<ISocketMessage>();
  /**
   * El server rechazó el token. Lo escucha HttpService para refrescar la
   * sesión: una pantalla que vive sólo del socket (el unifilar) no hace pedidos
   * HTTP, así que ningún 401 dispararía el refresh y quedaría congelada.
   */
  public tokenRechazado$ = new Subject<string>();
  // Token con el que se intentó la última conexión (el que el server rechazó).
  private tokenHandshake = '';
  /**
   * El socket volvió después de un corte: los avisos del corte se perdieron y
   * nada los repone (iFix manda un tag sólo cuando cambia). Los que cachean
   * vuelven a pedir. Llega con un retraso al azar de hasta 10 s para que, tras
   * un rollout del server, no pidan todas las pantallas a la vez.
   */
  public reconectado$ = new Subject<void>();
  private conectoAlgunaVez = false;
  /** El server revocó la sesión (usuario o cliente deshabilitado). Llega el motivo. */
  public sesionRevocada$ = new Subject<string>();

  constructor() {
    this.initWs();
  }

  // Se llama al arrancar, al loguearse y después de cada refresh del token:
  // cierra el socket anterior para no dejar dos abiertos.
  public initWs() {
    this.closeWS();
    if (!LoginService.getToken() || !websocketServer) {
      console.log('No hay token o websocketServer');
      return;
    }
    const socket = io(websocketServer, {
      path: websocketPath,
      transports: ['websocket', 'polling'],
      // Callback y no objeto: cada reconexión lee el token vigente.
      auth: (cb) => {
        this.tokenHandshake = LoginService.getToken();
        cb({ token: `Bearer ${this.tokenHandshake}` });
      },
    });
    socket.on('update', (message: ISocketMessage) =>
      this.socketMsg$.next(message),
    );
    socket.on('connect', () => {
      if (this.conectoAlgunaVez) {
        setTimeout(() => this.reconectado$.next(), Math.random() * 10000);
      }
      this.conectoAlgunaVez = true;
    });
    // El único cierre del que socket.io no se recupera solo.
    let revocada = false;
    socket.on('sesion-revocada', (p?: { motivo?: string }) => {
      revocada = true;
      this.sesionRevocada$.next(p?.motivo ?? '');
    });
    socket.on('disconnect', (reason) => {
      // Tras una revocación el server corta y no hay que reconectar.
      if (
        reason === 'io server disconnect' &&
        this.socket === socket &&
        !revocada
      ) {
        socket.connect();
      }
    });
    socket.on('connect_error', (error) => {
      console.log(`WebSocket connect_error: ${error.message}`);
      if (error.message === 'sesion-revocada') {
        revocada = true;
        this.sesionRevocada$.next(
          (error as { data?: { motivo?: string } }).data?.motivo ?? '',
        );
        return;
      }
      if (error.message === 'No autorizado') {
        this.tokenRechazado$.next(this.tokenHandshake);
      }
      // Si el server rechazó el token, socket.io no reintenta solo.
      if (!socket.active) {
        setTimeout(() => {
          if (this.socket === socket) socket.connect();
        }, 10000);
      }
    });
    this.socket = socket;
  }

  // Después de un refresh: el socket conectado sigue sirviendo (el token se
  // valida sólo al conectar); si estaba caído o rechazado, reconecta ya con el
  // token nuevo en vez de esperar el reintento.
  public asegurarConexion() {
    if (!this.socket?.connected) this.initWs();
  }

  public closeWS() {
    this.socket?.disconnect();
    this.socket = undefined;
  }

  public getMessage(): Observable<ISocketMessage> {
    return this.socketMsg$.asObservable();
  }
}
